import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { 
  getAllCategories,
  getCategoryById,
  addCategory,
  updateCategory,
  deleteCategory
} from '../services/categoryService';
import { Category } from '../types/category';
import { useAuth } from './AuthContext';

// Define context type
interface CategoryContextType {
  categories: Category[];
  categoryNames: string[];
  loading: boolean;
  error: string | null;
  fetchCategories: (forceRefresh?: boolean) => Promise<void>;
  fetchCategoryById: (id: string) => Promise<Category | null>;
  createCategory: (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Category>;
  modifyCategory: (id: string, category: Partial<Omit<Category, 'id' | 'createdAt'>>) => Promise<void>;
  removeCategory: (id: string) => Promise<void>;
}

// Create context
const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

// Storage keys
const CATEGORIES_INIT_KEY = 'jewelry_inventory_categories_initialized';

// Helper to check if categories have been initialized
const getCategoriesInitialized = (): boolean => {
  try {
    return localStorage.getItem(CATEGORIES_INIT_KEY) === 'true';
  } catch (error) {
    console.warn('Could not access localStorage:', error);
    return false;
  }
};

// Helper to mark categories as initialized
const setCategoriesInitialized = (initialized: boolean): void => {
  try {
    localStorage.setItem(CATEGORIES_INIT_KEY, initialized ? 'true' : 'false');
  } catch (error) {
    console.warn('Could not write to localStorage:', error);
  }
};

// Custom hook for using the context
export const useCategory = (): CategoryContextType => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};

// Category Provider props
interface CategoryProviderProps {
  children: ReactNode;
}

// Category Provider component
export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Initialize from localStorage
  const [initialized, setInitialized] = useState<boolean>(() => getCategoriesInitialized());
  const { user } = useAuth();

  // Memoize category names to prevent unnecessary recalculations
  const categoryNames = useMemo(() => {
    // Extract unique category names using Set, then convert to Array for compatibility
    const uniqueNamesSet = new Set(categories.map(cat => cat.name));
    return Array.from(uniqueNamesSet);
  }, [categories]);

  // Define fetchCategories as a memoized callback to prevent unnecessary re-renders
  const fetchCategories = useCallback(async (forceRefresh: boolean = false): Promise<void> => {
    // Don't fetch if we're already loading or if it's already initialized (unless forced)
    if (loading || (initialized && !forceRefresh)) return;
    
    setLoading(true);
    setError(null);
    try {
      const result = await getAllCategories();
      setCategories(result);
      // Mark as initialized after successful fetch
      setInitialized(true);
      setCategoriesInitialized(true);
    } catch (error) {
      setError('Kategoriler yüklenirken bir hata oluştu.');
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, initialized]);

  // Memoized function to fetch a category by ID with local cache first
  const fetchCategoryById = useCallback(async (id: string): Promise<Category | null> => {
    // First try to find the category in our local state
    const localCategory = categories.find(cat => cat.id === id);
    if (localCategory) {
      return localCategory;
    }
    
    // Only make a network request if not found locally
    setLoading(true);
    setError(null);
    
    try {
      const result = await getCategoryById(id);
      return result;
    } catch (error) {
      setError('Kategori yüklenirken bir hata oluştu.');
      console.error('Error fetching category by ID:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [categories]);

  // Create a new category
  const createCategory = useCallback(async (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<Category> => {
    setLoading(true);
    setError(null);
    try {
      const result = await addCategory(category);
      setCategories(prev => [result, ...prev]);
      return result;
    } catch (error) {
      setError('Kategori eklenirken bir hata oluştu.');
      console.error('Error creating category:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing category
  const modifyCategory = useCallback(async (id: string, category: Partial<Omit<Category, 'id' | 'createdAt'>>): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await updateCategory(id, category);
      setCategories(prev => prev.map(c => 
        c.id === id 
          ? { ...c, ...category, updatedAt: new Date() } 
          : c
      ));
    } catch (error) {
      setError('Kategori güncellenirken bir hata oluştu.');
      console.error('Error updating category:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a category
  const removeCategory = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await deleteCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      setError('Kategori silinirken bir hata oluştu.');
      console.error('Error deleting category:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear initialization when user logs out
  useEffect(() => {
    if (!user) {
      setCategories([]);
      setInitialized(false);
      setCategoriesInitialized(false);
    }
  }, [user]);

  // Load categories when component mounts if user is logged in and not initialized
  useEffect(() => {
    if (user && !initialized && categories.length === 0) {
      fetchCategories();
    }
  }, [user, initialized, categories.length, fetchCategories]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    categories,
    categoryNames,
    loading,
    error,
    fetchCategories,
    fetchCategoryById,
    createCategory,
    modifyCategory,
    removeCategory
  }), [
    categories, 
    categoryNames, 
    loading, 
    error, 
    fetchCategories, 
    fetchCategoryById, 
    createCategory, 
    modifyCategory, 
    removeCategory
  ]);

  return (
    <CategoryContext.Provider value={contextValue}>
      {children}
    </CategoryContext.Provider>
  );
}; 